/**
 * B2B External API admin — shared domain types.
 *
 * Vocabulary (glossary: `docs/migration/CONTEXT.md` → External API):
 * - **API Client** — a B2B organisation holding credentials (`ApiKeys` row).
 * - **Client Key** — its public identifier (`TR_<NAME>`), sent as `X-APIKey`.
 * - **Client Token** — its long-lived secret, sent as `X-APIToken`. Never leaves the
 *   API except through an explicit, audited reveal or rotate.
 * - **Grant** — permission to call one Endpoint. **Feature Flag** — response-shape
 *   toggle. **Scope** — country / news-topic limit. **Endpoint** / **Product** — catalog.
 */

// ── Authentication type ─────────────────────────────────────────

export const AUTH_TYPES = {
  1: "full",
  2: "limited",
  4: "denied",
  8: "singleFeature",
} as const;

export type AuthTypeId = keyof typeof AUTH_TYPES;
export type AuthTypeName = (typeof AUTH_TYPES)[AuthTypeId];

export const AUTH_TYPE_LABELS: Record<AuthTypeName, string> = {
  full: "Full — every endpoint",
  limited: "Limited",
  denied: "Denied",
  singleFeature: "Single feature — only granted endpoints",
};

// ── The API Client aggregate ────────────────────────────────────

export interface Grant {
  /** `SingleFeaturePermissions.Id` — app-assigned, not identity. */
  id: number;
  /** The path prefix the runtimes match against, e.g. `/api/stocks/marketMovers`. */
  featureUrl: string;
  /** `FeatureUrl.FeatureUrlID` when the grant points at a catalog Endpoint. */
  featureUrlId: number | null;
  /**
   * True when this grant is a bare path prefix rather than an exact catalog
   * Endpoint. Legacy rows only: the UI shows these read-only with their expansion,
   * and new grants are always exact Endpoints.
   */
  isPrefix: boolean;
}

export interface FeatureFlag {
  featureId: number;
  name: string;
  enabled: boolean;
  /** Typed payload for value-carrying flags (e.g. `AccessTokenExpirationMinutes`). */
  customValue: string | null;
}

export interface CountryScope {
  countryId: number;
  isActive: boolean;
}

export interface NewsScope {
  newsCategoryId: number;
  slug: string;
  isActive: boolean;
}

/** List-row view: never carries the Client Token. */
export interface ApiClientSummary {
  id: number;
  key: string;
  authType: AuthTypeName;
  isActive: boolean;
  tenantId: number | null;
  environment: string | null;
  grantCount: number;
  /** A SingleFeature client with no grants gets 401 on every endpoint. */
  hasNoGrants: boolean;
  /** Set once the usage layer (STORY-2104) is wired; null until then. */
  lastSeenAt: string | null;
  /** True while a cache eviction for this client is still pending. */
  propagationPending: boolean;
}

/** The full aggregate behind one client's detail page. */
export interface ApiClient {
  id: number;
  key: string;
  /** Masked: last 4 characters only. The full value is never sent on a read. */
  tokenMasked: string;
  authType: AuthTypeName;
  isActive: boolean;
  period: number;
  benchmark: number;
  allowCanadian: boolean;
  allowEuropean: boolean;
  includeUnranked: boolean;
  tenantId: number | null;
  environment: string | null;
  grants: Grant[];
  featureFlags: FeatureFlag[];
  countries: CountryScope[];
  newsCategories: NewsScope[];
}

/** Scalars editable on the Overview tab. */
export interface ApiClientScalars {
  authType: AuthTypeName;
  isActive: boolean;
  period: number;
  benchmark: number;
  allowCanadian: boolean;
  allowEuropean: boolean;
  includeUnranked: boolean;
  tenantId: number | null;
  environment: string | null;
}

// ── The catalog ─────────────────────────────────────────────────

export interface CatalogEndpoint {
  featureUrlId: number;
  featureUrl: string;
  description: string | null;
  products: string[];
  /** Title from the docs entry, when one exists (STORY-2103). */
  docTitle: string | null;
  docSlug: string | null;
}

/**
 * How well one Endpoint is covered — the single signal the Catalog page filters on.
 *
 * The two that cost money are `undocumented` (a customer can be granted it but cannot
 * discover it) and `orphanDoc` (a documented path with no `FeatureUrl` row, so it is
 * published on the docs site and **cannot be granted to anybody**). The legacy tool
 * showed neither: the SQL catalog and the WordPress docs never met.
 */
export type CatalogCoverage =
  | "documented"
  | "draft"
  | "deprecated"
  | "undocumented"
  | "orphanDoc";

export const CATALOG_COVERAGE_LABELS: Record<CatalogCoverage, string> = {
  documented: "Documented",
  draft: "Doc in draft",
  deprecated: "Deprecated",
  undocumented: "No docs",
  orphanDoc: "No FeatureUrl",
};

/** The docs half of a catalog row. Null when nobody has documented the Endpoint. */
export interface CatalogRowDoc {
  slug: string;
  title: string;
  summary: string;
  status: "draft" | "published" | "deprecated";
  method: "GET" | "POST";
  categoryId: string;
  categoryName: string;
  visibleOnDocs: boolean;
  updatedAt: string;
  updatedBy: string;
}

/** One row of the Catalog page: a `FeatureUrl` row, its docs entry, or one without the other. */
export interface CatalogRow {
  /** Null for an `orphanDoc` — a documented path with no permission behind it. */
  featureUrlId: number | null;
  /** The `FeatureUrl` path, or the documented path when there is no `FeatureUrl`. */
  path: string;
  description: string | null;
  products: string[];
  doc: CatalogRowDoc | null;
  coverage: CatalogCoverage;
}

export interface CatalogCounters {
  endpoints: number;
  documented: number;
  draft: number;
  deprecated: number;
  undocumented: number;
  orphanDocs: number;
  /** Guide pages — they have no `FeatureUrl` by design, so they are not catalog rows. */
  guides: number;
}

export interface CatalogView {
  rows: CatalogRow[];
  counters: CatalogCounters;
  /** Every doc category in use, in sidebar order. */
  categories: { id: string; name: string; order: number }[];
  /** Every SQL Product name, for the "group by Product" view. */
  products: string[];
  /** False when the docs collection could not be read; rows then show paths only. */
  docsAvailable: boolean;
}

// ── Write modes ─────────────────────────────────────────────────

/**
 * How far a given operation is allowed to go, per environment
 * ([ADR-0027](../../../docs/migration/adr/0027-external-api-db-safe-development.md)):
 * - `readonly` — refuse the write; the section still reads.
 * - `dry-run` — execute inside a transaction that is **rolled back**, then record and
 *   email the diff that *would* have applied.
 * - `live` — commit.
 */
export type B2bWriteMode = "readonly" | "dry-run" | "live";

export const B2B_OPERATIONS = [
  "createClient",
  "updateClient",
  "setAccess",
  "rotateToken",
  "revealToken",
  "deactivate",
  "catalog",
  "publishDocs",
  "settings",
  "revert",
] as const;

export type B2bOperation = (typeof B2B_OPERATIONS)[number];

/** Operations promoted to `live` one at a time, in this order (ADR-0027 decision 3). */
export const B2B_LIVE_PROMOTION_ORDER: B2bOperation[] = [
  "createClient",
  "setAccess",
  "rotateToken",
  "deactivate",
];

export type B2bWriteModes = Record<B2bOperation, B2bWriteMode>;

export function defaultWriteModes(): B2bWriteModes {
  return Object.fromEntries(B2B_OPERATIONS.map((op) => [op, "readonly"])) as B2bWriteModes;
}

// ── Access presets ──────────────────────────────────────────────

/** A named starting point for a new client, so nobody assembles grants by hand. */
export interface AccessPreset {
  id: string;
  name: string;
  description: string;
  authType: AuthTypeName;
  featureUrlIds: number[];
  flags: { name: string; enabled: boolean; customValue: string | null }[];
  countryIds: number[];
  newsCategoryIds: number[];
}

// ── Change log ──────────────────────────────────────────────────

export type PropagationStatus = "not-needed" | "ok" | "pending" | "failed";

export interface RefreshTargetResult {
  /** Target name from `B2B_REFRESH_TARGETS`, e.g. "v1" / "v2". */
  target: string;
  status: "ok" | "failed" | "skipped";
  detail?: string;
  /** Which credential was evicted — the old Client Key or the new one. */
  phase: "before" | "after";
}

export interface DiffEntry {
  /** Dotted path, e.g. `isActive`, `grants`, `featureFlags.ShowISIN`. */
  path: string;
  before: unknown;
  after: unknown;
}

export interface B2bChange {
  id: string;
  at: string;
  actor: string;
  operation: B2bOperation;
  mode: B2bWriteMode;
  clientId: number | null;
  clientKey: string | null;
  /** Human summary, e.g. "Added 3 grants, removed 1". */
  summary: string;
  diff: DiffEntry[];
  propagation: PropagationStatus;
  refreshResults: RefreshTargetResult[];
  notify: { status: "sent" | "failed" | "skipped"; detail?: string } | null;
  /** Set on a revert; points at the change being undone. */
  revertOf: string | null;
  /** Set on the change that a later revert undid. */
  revertedBy: string | null;
  requestId: string | null;
}

// ── Revert ──────────────────────────────────────────────────────

/**
 * Whether a recorded change can be undone.
 *
 * Lives in shared because the UI decides whether to *show* Revert and the API decides
 * whether to *allow* it. Two copies of this rule drift apart the first time one changes,
 * and the symptom is a visible button that answers 409.
 *
 * - A change that ran in `dry-run` or `readonly` never touched the database.
 * - A `rotateToken` / `revealToken` has nothing to restore: the change record masks the
 *   token deliberately, so an old secret can never be recovered from the audit trail.
 * - Reverting a `createClient` would mean deleting a client, which this tool never does.
 * - `catalog`, `publishDocs` and `settings` snapshots are not client state.
 */
export const REVERTABLE_OPERATIONS: B2bOperation[] = [
  "updateClient",
  "setAccess",
  "deactivate",
];

export function canRevert(change: {
  operation: B2bOperation;
  mode: B2bWriteMode;
  revertedBy?: string | null;
}): boolean {
  if (change.mode !== "live") return false;
  if (change.revertedBy) return false;
  return REVERTABLE_OPERATIONS.includes(change.operation);
}

// ── Section config (admin_config document `b2b`) ────────────────

export interface B2bConfig {
  writeModes: B2bWriteModes;
  /** Recipients of the change notification email. */
  notifyTo: string[];
  presets: AccessPreset[];
  /** Client Key of the canary used to verify propagation end to end. */
  canaryKey: string | null;
  /**
   * The canary's Client Token, **encrypted at rest** with an `AUTH_SECRET`-derived key.
   * It is a live credential on a real runtime, so it is never stored in plaintext and
   * never leaves the API — the UI only ever sees whether one is configured.
   */
  canaryTokenEncrypted: string | null;
  /** The last probe result, so the Settings page can show it without re-running. */
  canaryLastResult: CanaryRun | null;
  updatedAt: string | null;
  updatedByEmail: string | null;
}

// ── Canary probe (ADR-0027 decision 3) ──────────────────────────

/**
 * One assertion the probe made: "on this runtime, with these credentials, this endpoint
 * answered X, and we expected Y".
 */
export interface CanaryStep {
  /** Refresh-target name, e.g. `v1` / `v2`. */
  target: string;
  step: string;
  url: string;
  expectedStatus: number[];
  actualStatus: number | null;
  ok: boolean;
  detail?: string;
}

export interface CanaryRun {
  at: string;
  actor: string;
  mode: B2bWriteMode;
  /** `check` probes the current state; `cycle` grants, probes, revokes and probes again. */
  kind: "check" | "cycle";
  ok: boolean;
  /** Set when the run could not be attempted at all (no canary, dry-run, no targets). */
  skipped?: string;
  steps: CanaryStep[];
}

export const B2B_CONFIG_ID = "b2b";

export function defaultB2bConfig(): B2bConfig {
  return {
    writeModes: defaultWriteModes(),
    notifyTo: [],
    presets: [],
    canaryKey: null,
    canaryTokenEncrypted: null,
    canaryLastResult: null,
    updatedAt: null,
    updatedByEmail: null,
  };
}

// ── Landing-page summary ────────────────────────────────────────

export interface B2bSummary {
  clients: { total: number; active: number; inactive: number; withoutGrants: number };
  changesLast24h: number;
  propagationPending: number;
  notifyFailures: number;
  recentChanges: B2bChange[];
  writeModes: B2bWriteModes;
}

// ── Helpers ─────────────────────────────────────────────────────

export function authTypeName(id: number): AuthTypeName {
  const name = AUTH_TYPES[id as AuthTypeId];
  if (!name) throw new Error(`Unknown AuthenticationType ${id}`);
  return name;
}

export function authTypeId(name: AuthTypeName): AuthTypeId {
  const entry = Object.entries(AUTH_TYPES).find(([, n]) => n === name);
  if (!entry) throw new Error(`Unknown auth type ${name}`);
  return Number(entry[0]) as AuthTypeId;
}

/**
 * A grant is a **prefix** when its path is not an exact catalog Endpoint — the
 * runtimes match with `path.Contains(featureUrl)`, so `/api/stocks/` unlocks every
 * `/api/stocks/*` route. Legacy rows only; the UI never creates one.
 */
export function isPrefixGrant(featureUrl: string, catalogPaths: Set<string>): boolean {
  return !catalogPaths.has(normalizeFeatureUrl(featureUrl));
}

/**
 * The runtimes' normalisation, replicated exactly: lowercase, leading and trailing
 * slash. Both C# implementations do this before the substring comparison
 * (`ApiAuthenticator` in v1, `HttpContextExtensions` in v2).
 */
export function normalizeFeatureUrl(url: string): string {
  let value = url.trim().toLowerCase();
  if (!value.startsWith("/")) value = `/${value}`;
  if (!value.endsWith("/")) value = `${value}/`;
  return value;
}

/** Mask a Client Token for display and for the change log: `••••1234`. */
export function maskToken(token: string | null | undefined): string {
  if (!token) return "";
  const tail = token.slice(-4);
  return `••••${tail}`;
}
